import * as bodyScrollLock from 'body-scroll-lock'
import { AnimatePresence, motion } from 'framer-motion'
import { navigate } from 'gatsby'
import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-use'
import { useLang } from '../../hooks/useLang'
import { useNavHover } from '../../hooks/useNavHover'
import { useTranslations } from '../../hooks/useTranslations'
import ContactPeopleIcon from '../../images/contact-people.png'
import { useToolOverlay } from '../Context/ToolOverlayContext'
import MobileNavLink from '../atoms/MobileNavLink'

const MobileMenu = ({ nav, alternates, onOpenTool, onClose }) => {
  const ref = useRef(null)
  const location = useLocation()
  const locale = useLang()
  const { setContextSectionLimit } = useToolOverlay()
  const { getHoverStart, getHoverEnd } = useNavHover(nav)
  const translations = useTranslations()

  const openToolSection = (section, label) => {
    setContextSectionLimit(section)
    onOpenTool(label)
  }

  useEffect(() => {
    bodyScrollLock.disableBodyScroll(ref.current)

    return () => bodyScrollLock.clearAllBodyScrollLocks()
  }, [])

  return (
    <>
      <motion.div
        className="fixed inset-0 z-[910] bg-white opacity-80"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      <motion.div
        ref={ref}
        className="fixed top-0 left-0 right-0 z-[920] max-h-screen py-5 overflow-y-scroll bg-white shadow-box"
        initial={{ height: 0 }}
        animate={{ height: 'auto ' }}
        exit={{ height: 0 }}
        transition={{ type: 'ease' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="text-white bg-black rounded-full w-[30px] h-[30px] fixed top-4 right-4 flex items-center justify-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            fill="none"
            viewBox="0 0 22 22"
          >
            <path
              stroke="#fff"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 5l6 6-6 6M17 5l-6 6 6 6"
            ></path>
          </svg>
        </button>

        <AnimatePresence initial={false}>
          <ul className="px-16">
            {nav
              .filter(
                ({ __typename }) =>
                  __typename ===
                  'CraftContent_navigation_rootLevelLink_BlockType',
              )
              .map(({ id, target, emphasize }) => (
                <MobileNavLink
                  key={id}
                  id={id}
                  target={target}
                  emphasize={emphasize}
                  subNav={nav.slice(getHoverStart(id), getHoverEnd(id))}
                />
              ))}
          </ul>
        </AnimatePresence>

        <div className="px-16 py-2 mt-2 divide-y bg-gray-1 divide-gray-4 text-[15px]">
          <div className="py-4">
            <button
              type="button"
              className="flex items-center"
              onClick={() =>
                openToolSection('emergency', translations.reportFault)
              }
            >
              <div className="w-[22px] flex items-center justify-center mr-2.5">
                <div className="w-4 h-4 rounded-full bg-red" />
              </div>
              {translations.reportFault}
            </button>
          </div>
          <div className="py-4">
            <button
              type="button"
              className="flex items-center"
              onClick={() =>
                openToolSection('people', translations.personalContact)
              }
            >
              <img
                src={ContactPeopleIcon}
                alt=""
                className="w-[22px] h-auto mr-2.5"
              />
              {translations.personalContact}
            </button>
          </div>
          <div className="py-4">
            <button
              type="button"
              className="flex items-center"
              onClick={() =>
                openToolSection('planningtool', translations.planningTools)
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                fill="none"
                viewBox="0 0 22 22"
                className="mr-2.5"
              >
                <path
                  fill="#000"
                  fillRule="evenodd"
                  d="M16.337 8.838L5.175 20H2v-3.175L13.162 5.663l3.175 3.175zm1.415-1.415l1.54-1.54a1 1 0 000-1.415l-1.76-1.76a1 1 0 00-1.414 0l-1.541 1.54 3.175 3.175z"
                  clipRule="evenodd"
                ></path>
              </svg>
              {translations.planningTools}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end px-16 pt-5">
          <div className="inline-flex items-center bg-black ">
            <select
              className="text-white bg-black border-0 appearance-none font-bold text-[11px] uppercase pr-0 bg-none focus:outline-none"
              value={
                alternates && locale in alternates
                  ? alternates[locale]
                  : `/${locale}`
              }
              onChange={e => {
                window.localStorage.setItem('langOverride', '1')
                navigate(e.target.value)
              }}
            >
              <option
                value={alternates && 'de' in alternates ? alternates.de : '/de'}
              >
                Deutsch
              </option>
              <option
                value={alternates && 'fr' in alternates ? alternates.fr : '/fr'}
              >
                Français
              </option>
              <option
                value={alternates && 'it' in alternates ? alternates.it : '/it'}
              >
                Italiano
              </option>
            </select>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="7"
              fill="none"
              viewBox="0 0 11 7"
              className="mr-3 w-2.5 h-auto ml-2"
            >
              <path
                stroke="#fff"
                strokeLinecap="round"
                strokeWidth="2"
                d="M1 1l4.5 4.5L10 1"
              ></path>
            </svg>
          </div>
        </div>
      </motion.div>
    </>
  )
}

export default MobileMenu
